// ts-rest contract for the 8-stage onboarding pipeline (PRD #69).
//
// The pipeline takes a prospect from initial enquiry through to a fully
// onboarded investing client. Endpoints under /api/onboarding/*:
//   GET  /api/onboarding/cases               — list cases (optional filters)
//   GET  /api/onboarding/cases/:id           — single case detail
//   POST /api/onboarding/cases               — create a new enquiry (stage 1)
//   POST /api/onboarding/cases/:id/remove     — remove a case (reason required)
//   POST /api/onboarding/cases/:id/transition — move a case between stages
//   POST /api/onboarding/cases/:id/post-meeting-note — capture a post-meeting note
//   POST /api/onboarding/cases/:id/fact-find/remind  — send a fact-find reminder
//   GET  /api/onboarding/cases/:id/registers  — the four SEP-243 slice-7 registers
//   GET  /api/onboarding/analytics            — pipeline analytics rollup

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

// SEP-243 section 2 - the closed sets the enquiry captures. The risk list is
// the SAME one the Internal Review reads, so the client's intention and the
// IM's recommendation are directly comparable, and neither seeds the other
// (SEP-243 section 24).
import {
    isaIntentSchema,
    isaSelectionSchema,
    investmentObjectiveSchema,
} from '../enquiry-intent-schemas';
import { riskProfileSchema } from '../risk-profile';
import { suitabilityLifecycleSchema } from '../suitability-assessment-lifecycle';
import { internalReviewBranchesSchema } from '../internal-review-branches';
import { finalApprovalHandoverSchema } from '../final-approval';

// SEP-243 sections 4 and 5 - the two document registers. The catalogue, the
// wire shapes and the pure summary folds live in one module, so the panel, the
// controller and the later approval gate read one computation.
import {
    providedDocumentEntrySchema,
    providedDocumentAnswerSchema,
    providedDocumentDeliveryMethodSchema,
    providedDocumentSendSchema,
    firmDocumentVersionSchema,
    receivedDocumentRequirementSchema,
    packServiceTypeSchema,
} from '../onboarding-document-registers';
import { signingBlockerSchema, signingReadinessSchema } from '../signing-readiness';
import {
    APPLICATION_FORM_LINK_MARKER,
    hasApplicationFormLinkMarker,
} from '../application-form-invitation-email';
import {
    caseDocumentAcknowledgementCountsSchema,
    caseDocumentAcknowledgementsResponseSchema,
} from '../portal-document-acknowledgement';
// SEP-243 decision D4 - the Compliance copy seam.
import { complianceCopyScopeSchema, resolvedComplianceCopySchema } from '../compliance-copy';

const c = initContract();

// ---------------------------------------------------------------------------
// Stage + status enums
// ---------------------------------------------------------------------------

/** The eight stages a case walks, plus the one that is retired. */
export const onboardingPipelineStageSchema = z.enum([
    'enquiry',
    'first_meeting',
    // SEP-289. The client application form, before the fact find. A client
    // who applies online arrives with a submission. A client who signs up with
    // the IM or the EA gets an invitation to the same online form.
    'application_form',
    // Stage 4, the Internal Review (SEP-293 section 7). The id keeps its first
    // name, because a new id needs a migration of the stored stage and of each
    // audit entry.
    'fact_find',
    // RETIRED with SEP-243 section 16. No case enters this stage: the AML and
    // KYC work runs on the track beside the case, and the value is kept only so
    // a row written before the change still parses. It is dropped in a later
    // release, once no running image can write it.
    'kyc_aml',
    'suitability',
    'signing',
    'account_opening',
    // RETIRED with SEP-346. No case enters this stage: Account opening is the
    // last stage, and the case finishes with the `completeCase` route. The
    // value stays so that a row and an audit entry written before the change
    // still parse.
    'portal_access',
]);
export type OnboardingPipelineStage = z.infer<typeof onboardingPipelineStageSchema>;

export const onboardingCaseStatusSchema = z.enum(['active', 'stalled', 'completed']);
export type OnboardingPipelineCaseStatus = z.infer<typeof onboardingCaseStatusSchema>;

/**
 * Where an enquiry came from.
 *
 * `Online client application` is the CLIENT-LED route of SEP-263 (decision
 * D11). The investment manager must be able to tell an application the client
 * filled in from an enquiry they raised themselves (SEP-263.24), so the origin
 * is a value of this enum and a column of the case. It is never inferred from a
 * linked submission, because an inference would read as an absence on every
 * surface that does not join.
 *
 * NOTHING BUT THE ONLINE ROUTE MAY WRITE IT. The staff enquiry dialog offers
 * the five values above it and no more.
 */
export const onboardingEnquirySourceSchema = z.enum([
    'IFA referral',
    'Client referral',
    'Professional introducer',
    'Direct enquiry',
    'Event',
    'Online client application',
]);
export type OnboardingEnquirySource = z.infer<typeof onboardingEnquirySourceSchema>;

/** The origin value the client-led route stamps (SEP-263.24, decision D11). */
export const ONLINE_CLIENT_APPLICATION_SOURCE = 'Online client application' as const;

/**
 * The five origins a member of staff may choose in the enquiry dialog.
 *
 * The staff intake schema is this narrower enum and NOT the whole set, so a
 * staff request can never claim the online origin. Only the submission of a
 * client application writes that value, and it writes it on the server.
 */
export const staffEnquirySourceSchema = z.enum([
    'IFA referral',
    'Client referral',
    'Professional introducer',
    'Direct enquiry',
    'Event',
]);
export type StaffEnquirySource = z.infer<typeof staffEnquirySourceSchema>;

/** The five origins the enquiry dialog offers, in the order it prints them. */
export const STAFF_ENQUIRY_SOURCES: readonly StaffEnquirySource[] =
    staffEnquirySourceSchema.options;

/**
 * One possible duplicate that the online route found and did NOT act on
 * (decision D2, SEP-263.51).
 *
 * An online application ALWAYS creates a new prospect `Client`. It never links
 * to an existing one, because a stranger who types the email address of a real
 * client would otherwise be attached to that client's record and their portal
 * password replaced. The match still runs, and it lands here for the
 * investment manager to settle by hand. Merging is a human decision.
 */
export const onboardingPossibleDuplicateSchema = z.object({
    /** What matched: the email address of the application, or its telephone number. */
    kind: z.enum(['email', 'phone']),
    /** The sentence the case detail prints, in the words of the firm. */
    message: z.string(),
    /** The `Client.id` that matched, when the match was a Client. */
    clientId: z.string().nullable().optional(),
    /** The `Contact.id` that matched, when the match was a Contact. */
    contactId: z.string().nullable().optional(),
    /** The name of the matched record, so the manager reads who it is. */
    name: z.string().nullable().optional(),
});
export type OnboardingPossibleDuplicate = z.infer<typeof onboardingPossibleDuplicateSchema>;

export const kycResultStatusSchema = z.enum(['pass', 'refer', 'fail']);

/**
 * The position of a case on the AML and KYC track (SEP-243 sections 13 to 16).
 *
 * `ready` means the IM or EA handed the case to Ops. `cleared` is the only
 * value that opens the `suitability -> signing` gate. `failed` is terminal.
 */
export const amlTrackStatusSchema = z.enum([
    'not_started',
    'ready',
    'running',
    'awaiting_results',
    'referred',
    'cleared',
    'failed',
]);
export type AmlTrackStatus = z.infer<typeof amlTrackStatusSchema>;
export type KycResultStatus = z.infer<typeof kycResultStatusSchema>;

/**
 * The track positions from which Ops may run the KYC and AML vendor.
 *
 * The `RecordCaseAmlVendorResult` Action is the authority, and it holds the
 * same list as `AML_VENDOR_RUN_FROM`. A server test pins the two lists to each
 * other. The copy is here because the browser reads the contract and never the
 * ontology, and a button that the Action refuses must not look pressable.
 *
 * `not_started` waits on the hand-over, `cleared` is done, and `failed` is
 * terminal.
 */
export const AML_VENDOR_RUN_FROM: readonly AmlTrackStatus[] = [
    'ready',
    'running',
    'awaiting_results',
    'referred',
];

/**
 * The results Ops may record for a check it ran OUTSIDE Septima (SEP-258).
 *
 * There is no manual `fail`: Ops does not decide what a hit means, Compliance
 * does. A `refer` opens the referral with the report attached, and Compliance
 * passes or fails it. `RecordCaseAmlManualResult` is the authority.
 */
export const amlManualResultSchema = z.enum(['pass', 'refer']);
export type AmlManualResult = z.infer<typeof amlManualResultSchema>;

/**
 * The source name of a check that Ops ran outside Septima. It stands in the
 * `vendor` slot of `kycResult` and of the referral. The ontology holds the same
 * constant (`MANUAL_AML_SOURCE` in `onboarding-aml-track-actions.ts`), and a
 * server test pins the two to each other.
 */
export const MANUAL_AML_SOURCE = 'manual';

// ---------------------------------------------------------------------------
// Stage artefacts
// ---------------------------------------------------------------------------

/**
 * The stored post-meeting note.
 *
 * FOUR FIELDS ARE HISTORY. SEP-243 sections 4, 10 and 11 moved the documents,
 * the conflicts answer, the classification and the high net worth answer onto
 * the four registers, and the capture form writes none of the four any more.
 * They stay OPTIONAL here so that a note the firm captured before the change
 * still reads, and so that a fresh note states nothing it does not know.
 */
export const postMeetingNoteSchema = z.object({
    capturedAt: z.string(),
    capturedBy: z.string(),
    clientAgenda: z.string(),
    classification: z.enum(['retail', 'professional']).optional(),
    hnwStatus: z.boolean().optional(),
    conflictsCheck: z.string().optional(),
    documentsHandedOver: z.array(z.string()).optional(),
});
export type PostMeetingNote = z.infer<typeof postMeetingNoteSchema>;

export const kycResultSchema = z.object({
    status: kycResultStatusSchema,
    vendor: z.string(),
    reference: z.string(),
    screenedAt: z.string(),
    sanctionsHits: z.number().int(),
    pepHits: z.number().int(),
    adverseMediaHits: z.number().int(),
    evidence: z.array(z.string()),
    eddRequired: z.boolean(),
});
export type KycResult = z.infer<typeof kycResultSchema>;

export const sourceOfFundsDeclarationSchema = z.object({
    declaredAt: z.string(),
    declaredBy: z.string(),
    primarySource: z.string(),
    narrative: z.string(),
    supportingEvidence: z.array(z.string()),
});
export type SourceOfFundsDeclaration = z.infer<typeof sourceOfFundsDeclarationSchema>;

// ---------------------------------------------------------------------------
// Audit + case
// ---------------------------------------------------------------------------

/**
 * The 409 of a transition. A refused move into Finalisation & Sign-off (the
 * `signing` stage) carries its blockers.
 */
export const transitionRefusalSchema = z.object({
    error: z.string(),
    code: z.string().optional(),
    blockers: z.array(signingBlockerSchema).optional(),
});

export const onboardingAuditEntrySchema = z.object({
    id: z.string(),
    from: onboardingPipelineStageSchema.nullable(),
    to: onboardingPipelineStageSchema,
    at: z.string(),
    by: z.string(),
    /**
     * The name of the IM that an executive assistant acted for. Only an entry
     * that an assistant wrote holds it.
     */
    onBehalfOf: z.string().optional(),
    note: z.string().optional(),
    /**
     * SEP-293 section 11. `blocked` records a refused move into Signing: the
     * stage did not change, and `reasons` holds the messages of the block.
     * SEP-346. `completed` records the end of the onboarding at Account
     * opening: the stage did not change, and the case status became
     * `completed`. An entry with no outcome is a move.
     */
    outcome: z.enum(['moved', 'blocked', 'completed']).optional(),
    reasons: z.array(z.string()).optional(),
});
export type OnboardingAuditEntry = z.infer<typeof onboardingAuditEntrySchema>;

/**
 * Where the application form of one case stands (SEP-289).
 *
 *   not_sent         : no invitation, no submission and no outside record
 *   invited          : the IM or the EA sent the client a link to the form
 *   submitted        : a submission names this case
 *   received_outside : the firm received the form outside Septima
 */
export const applicationFormStatusSchema = z.enum([
    'not_sent',
    'invited',
    'submitted',
    'received_outside',
]);
export type ApplicationFormStatus = z.infer<typeof applicationFormStatusSchema>;

export const onboardingPipelineCaseSchema = z.object({
    id: z.string(),
    caseRef: z.string(),
    /**
     * Canonical `Client.id` this case owns — the prospect is modelled as a
     * `Client(onboarding:true)` from enquiry and linked via
     * `onboarding_pipeline_cases.client_id`. Surfaced on the wire so the
     * frontend can filter the prospect's calls and link to the Fact Find page.
     * Nullable/optional: a partial / unlinked row carries none.
     */
    clientId: z.string().nullable().optional(),
    prospectName: z.string(),
    applicantType: z.enum(['Individual', 'Joint', 'Trust', 'Entity']),
    stage: onboardingPipelineStageSchema,
    status: onboardingCaseStatusSchema,
    /** ISO timestamp the case entered its current stage. Drives SLA + WARDEN. */
    stageEnteredAt: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    // Commercial
    source: onboardingEnquirySourceSchema,
    /**
     * The instant an ONLINE application arrived (SEP-263.24, decision D11).
     * The Stage 1 card prints it beside the origin. It is absent on every case
     * a member of staff raised, because a staff enquiry has no arrival.
     */
    arrivedAt: z.string().nullable().optional(),
    /**
     * The possible duplicates the online route found and left for the manager
     * (SEP-263.51). Empty on a staff-raised case. The route NEVER links and
     * NEVER replaces a portal password; it records and moves on.
     */
    possibleDuplicates: z.array(onboardingPossibleDuplicateSchema).optional(),
    referrer: z.string().optional(),
    /** The indicative AUM. Null when nobody stated one. Never a zero stand-in. */
    initialAum: z.number().nullable(),
    /**
     * The free-text mandate of interest. It holds the words of every case the
     * firm raised before SEP-243, and the intake now writes the label of
     * `investmentObjective` into it. Read the three intent fields below for a
     * value a reader can compare or count.
     */
    mandate: z.string(),
    // The intent the enquiry captures (SEP-243 section 2). Each one is absent
    // on a case from before the change: the ISA answer then reads `not_asked`,
    // and the other two read as "not captured".
    isaSelection: isaSelectionSchema.optional(),
    investmentObjective: investmentObjectiveSchema.optional(),
    intendedRiskProfile: riskProfileSchema.optional(),
    /**
     * Is the client a business owner, as the Stage 1 intake of the case
     * recorded it? The intake asks it no more: the fact find asks it now, and
     * its answer governs (SEP-263 decision D5). This field keeps the answer of
     * a case that the intake raised before that change, and the Internal Review
     * pre-populates from it when the fact find holds no answer. `null` is "not
     * captured".
     */
    isBusinessOwner: z.boolean().nullable().optional(),
    email: z.string(),
    phone: z.string().optional(),
    // Ownership
    assignedIm: z.string(),
    assignedImId: z.string().optional(),
    // The application form stage (SEP-289). DERIVED on each read: a
    // submission that names this case is `submitted`, a record of a form the
    // firm received outside Septima is `received_outside`, an invitation is
    // `invited`. The gate `application_form -> fact_find` reads it.
    applicationFormStatus: applicationFormStatusSchema.optional(),
    /** The instant of the submission that names this case, when one exists. */
    applicationFormSubmittedAt: z.string().optional(),
    applicationFormInvitedAt: z.string().optional(),
    /** The email address the last invitation went to. */
    applicationFormInvitedEmail: z.string().optional(),
    applicationFormReceivedOutsideAt: z.string().optional(),
    applicationFormReceivedOutsideNote: z.string().optional(),
    applicationFormReceivedOutsideByUserId: z.string().optional(),
    // Stage 4 — the Internal Review: the fact find branch
    factFindStatus: z.enum(['not_sent', 'sent', 'in_progress', 'complete']),
    factFindReminders: z.number().int(),
    factFindGaps: z.array(z.string()),
    // Stage-specific artefacts
    postMeetingNote: postMeetingNoteSchema.optional(),
    kycResult: kycResultSchema.optional(),
    sourceOfFunds: sourceOfFundsDeclarationSchema.optional(),
    // The AML and KYC track (SEP-243 section 16). The AML work runs BESIDE the
    // suitability work, so this is a per-CASE position and NOT a stage. A
    // pending track does not block the suitability work; an unresolved track
    // blocks `suitability -> signing`, which is the first gate past it.
    amlTrackStatus: amlTrackStatusSchema.optional(),
    readyForAmlAt: z.string().optional(),
    readyForAmlByUserId: z.string().optional(),
    amlClearedAt: z.string().optional(),
    amlClearedByUserId: z.string().optional(),
    /**
     * The AML report Ops attached when it recorded a check run outside
     * Septima (SEP-258). A SourceUpload id. Absent after a vendor run.
     */
    amlReportSourceUploadId: z.string().optional(),
    /**
     * The LAST Compliance decision on a referral of this case, copied onto the
     * case so the Desk reads it. The referral itself bars the Desk. A new
     * check clears the three fields. (SEP-258)
     */
    amlReferralOutcome: z.enum(['cleared', 'failed', 'escalated_to_mlro', 'declined']).optional(),
    amlReferralNote: z.string().optional(),
    amlReferralResolvedAt: z.string().optional(),
    // The final approval gate (SEP-243 sections 36 and 37). The two tracks join
    // here. `finalApprovedAt` is stamped by `CreateFinalApprovedSnapshot`, and
    // `checkPrerequisites` reads it for `signing -> account_opening`, so a case
    // reaches the account opening only through the gate.
    finalApprovedAt: z.string().optional(),
    finalApprovedByUserId: z.string().optional(),
    finalApprovedSnapshotId: z.string().optional(),
    // SEP-332, SEP-334. The newest hand-over of the final approval: sent to
    // Compliance, or returned to the IM. DERIVED on each read, and absent when
    // the case has none or when it holds its final approval.
    finalApprovalHandover: finalApprovalHandoverSchema.optional(),
    // SEP-337. The number of accounts that Ops recorded for the case. DERIVED
    // on each read for a case at Account opening, and absent on the other
    // stages. `checkCompletion` reads it before the case can finish.
    openedAccountCount: z.number().int().nonnegative().optional(),
    // SEP-318, decisions 4 and 6. Which documents of the signature pack are
    // signed: by DocuSign, or by an uploaded signed copy. DERIVED on each read
    // for a case at Signing or at Account opening, and absent on the other
    // stages. `checkPrerequisites` reads `mandate` for `signing ->
    // account_opening`, and `checkCompletion` reads `declaration` before the
    // case can finish.
    signatures: z.object({ mandate: z.boolean(), declaration: z.boolean() }).optional(),
    // SEP-344 item 9. The ISA answer with its source. DERIVED on the read of
    // one case: the enquiry, else the submitted application, else the approved
    // fact find.
    isaIntent: isaIntentSchema.optional(),
    // SEP-293 section 7. The state of the three sub-branches of stage 4, the
    // Internal Review: the Fact Find, the Vulnerability assessment and the
    // Suitability assessment. DERIVED on each read for a case on stage 4
    // (`deriveInternalReviewBranches`), and absent on the other stages.
    internalReview: internalReviewBranchesSchema.optional(),
    // SEP-293 sections 10 and 11. The portal acknowledgement of the required
    // documents of the case: the counts and the labels that are open. DERIVED
    // on each read (`summariseDocumentAcknowledgements`), and absent while
    // the feature is held.
    documentAcknowledgement: caseDocumentAcknowledgementCountsSchema.optional(),
    // The suitability branch of stage 4. Gate status for `fact_find → signing`, DERIVED
    // from the latest Internal Suitability Assessment for the case's client: no
    // assessment → not_started, a working draft → draft, a signed-off →
    // completed (which clears the gate).
    suitabilityAssessmentStatus: z.enum(['not_started', 'draft', 'completed']).optional(),
    // SEP-258. The eight-state lifecycle of the same latest Internal Review, and
    // its id, so the Internal Review panel prints where the review sits in the chain
    // IM -> Peer -> Compliance and opens the review chain. The legacy
    // three-value field above stays: the gate reads it.
    suitabilityLifecycle: suitabilityLifecycleSchema.optional(),
    suitabilityAssessmentId: z.string().optional(),
    portalProvisioned: z.boolean().optional(),
    welcomePackSentAt: z.string().optional(),
    // Misc
    blockingReason: z.string().optional(),
    agentTaskId: z.string().optional(),
    auditLog: z.array(onboardingAuditEntrySchema),
    // Terminal soft-removal (RemoveOnboardingCase). A removed case is retained
    // for audit but excluded from the list endpoint (and therefore the kanban,
    // counts and analytics); `getCase` still returns it so the detail page can
    // show the removed banner. `removedAt` null/absent on a live case.
    removedAt: z.string().optional(),
    removalReason: z.string().optional(),
    removedByUserId: z.string().optional(),
});
export type OnboardingPipelineCase = z.infer<typeof onboardingPipelineCaseSchema>;

// ---------------------------------------------------------------------------
// The AML referral (SEP-243 sections 14 and 15)
// ---------------------------------------------------------------------------

/** One vendor flag that a referral must answer. */
export const amlReferralFlagSchema = z.object({
    code: z.string(),
    category: z.enum(['sanctions', 'pep', 'adverse_media', 'identity', 'other']),
    matchCount: z.number().int(),
    detail: z.string().nullable(),
});
export type AmlReferralFlag = z.infer<typeof amlReferralFlagSchema>;

/**
 * One entry of the append-only note list. `information_request` is a request
 * that Compliance sends to Ops or to the Desk. Both kinds are permanent.
 */
export const amlReferralNoteSchema = z.object({
    id: z.string(),
    kind: z.enum(['note', 'information_request']),
    text: z.string(),
    at: z.string(),
    byUserId: z.string().nullable(),
    byName: z.string().nullable(),
});
export type AmlReferralNote = z.infer<typeof amlReferralNoteSchema>;

/**
 * The decision of Compliance on one referral. `cleared` is its pass and
 * `failed` is its fail (SEP-258): a fail sends the case back to the investment
 * manager with the note, and Ops records a new check once the client gives the
 * information. An escalation and a decline are the two other answers.
 */
export const amlReferralOutcomeSchema = z.enum([
    'cleared',
    'failed',
    'escalated_to_mlro',
    'declined',
]);
export type AmlReferralOutcome = z.infer<typeof amlReferralOutcomeSchema>;

/**
 * One compliance investigation into the AML result of one onboarding case. The
 * row is append-only: a note is added and never changed, and a resolved
 * referral never reopens.
 */
export const amlCaseReferralSchema = z.object({
    id: z.string(),
    caseId: z.string(),
    clientId: z.string().nullable(),
    caseRef: z.string(),
    prospectName: z.string(),
    status: z.enum(['open', 'resolved']),
    /** `ops_manual` is a check Ops ran outside Septima and referred (SEP-258). */
    raisedBy: z.enum(['vendor_refer', 'vendor_fail', 'ops_manual', 'compliance']),
    vendor: z.string().nullable(),
    vendorReference: z.string().nullable(),
    screenedAt: z.string().nullable(),
    flags: z.array(amlReferralFlagSchema),
    notes: z.array(amlReferralNoteSchema),
    openedAt: z.string(),
    openedByUserId: z.string().nullable(),
    outcome: amlReferralOutcomeSchema.nullable(),
    resolutionNote: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    resolvedByUserId: z.string().nullable(),
    sourceUploadIds: z.array(z.string()),
});
export type AmlCaseReferral = z.infer<typeof amlCaseReferralSchema>;

/**
 * The Ops request that runs the vendor.
 *
 * `outcome` is the SCENARIO override, and the scenario sandbox is the only
 * place that reads it. A configured vendor ignores it, and a deployment that
 * holds no vendor key refuses the run, so no deployment can pick its own
 * verdict.
 */
export const runAmlVendorRequestSchema = z.object({
    outcome: kycResultStatusSchema.optional(),
});
export type RunAmlVendorRequest = z.infer<typeof runAmlVendorRequestSchema>;

export const runAmlVendorResponseSchema = z.object({
    ok: z.boolean(),
    case: onboardingPipelineCaseSchema,
    /** The referral the verdict opened. Null on a clean pass. */
    referral: amlCaseReferralSchema.nullable(),
});
export type RunAmlVendorResponse = z.infer<typeof runAmlVendorResponseSchema>;

/**
 * One account that Operations opened for the client of a case (SEP-337). The
 * row changes only through a correction with a reason.
 */
export const openedAccountSchema = z.object({
    id: z.string(),
    caseId: z.string(),
    accountType: z.string(),
    custodian: z.string(),
    accountNumber: z.string(),
    /** `YYYY-MM-DD`. */
    liveDate: z.string(),
    recordedByUserId: z.string(),
    recordedAt: z.string(),
});
export type OpenedAccountDto = z.infer<typeof openedAccountSchema>;

export const recordOpenedAccountRequestSchema = z.object({
    accountType: z.string().trim().min(1),
    custodian: z.string().trim().min(1),
    accountNumber: z.string().trim().min(1),
    liveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type RecordOpenedAccountRequest = z.infer<typeof recordOpenedAccountRequestSchema>;

export const correctOpenedAccountRequestSchema = recordOpenedAccountRequestSchema.extend({
    /** Why the row changes. Mandatory. */
    reason: z.string().trim().min(1),
});
export type CorrectOpenedAccountRequest = z.infer<typeof correctOpenedAccountRequestSchema>;

export const openedAccountListResponseSchema = z.object({
    accounts: z.array(openedAccountSchema),
});
export type OpenedAccountListResponse = z.infer<typeof openedAccountListResponseSchema>;

export const amlReferralListResponseSchema = z.object({
    referrals: z.array(amlCaseReferralSchema),
});
export type AmlReferralListResponse = z.infer<typeof amlReferralListResponseSchema>;

export const amlReferralResponseSchema = z.object({
    ok: z.boolean(),
    referral: amlCaseReferralSchema,
    /** The case, so the caller sees the AML track move in one round trip. */
    case: onboardingPipelineCaseSchema.nullable(),
});
export type AmlReferralResponse = z.infer<typeof amlReferralResponseSchema>;

export const recordAmlReferralNoteRequestSchema = z.object({
    kind: z.enum(['note', 'information_request']),
    text: z.string().min(1),
    sourceUploadIds: z.array(z.string()).optional(),
});
export type RecordAmlReferralNoteRequest = z.infer<typeof recordAmlReferralNoteRequestSchema>;

export const resolveAmlReferralRequestSchema = z.object({
    outcome: amlReferralOutcomeSchema,
    resolutionNote: z.string().min(1),
});
export type ResolveAmlReferralRequest = z.infer<typeof resolveAmlReferralRequestSchema>;

// ---------------------------------------------------------------------------
// SEP-243 slice 7 - the two document registers, the classification and HNW
// record, the structured conflicts, and the Compliance copy seam.
// ---------------------------------------------------------------------------

/** The Compliance position on one conflict. */
export const conflictComplianceStatusSchema = z.enum([
    'not_required',
    'pending',
    'approved',
    'rejected',
]);
export type ConflictComplianceStatus = z.infer<typeof conflictComplianceStatusSchema>;

/** The structured conflicts answer of one case (SEP-243 section 11). */
export const onboardingConflictRecordSchema = z.object({
    id: z.string(),
    caseId: z.string(),
    clientId: z.string().nullable(),
    conflictIdentified: z.enum(['yes', 'no']),
    conflictType: z.string().nullable(),
    /** The copy entry that held the category list. Null for a placeholder. */
    conflictTypeCopyEntryId: z.string().nullable(),
    description: z.string().nullable(),
    partiesInvolved: z.array(z.string()),
    proposedMitigation: z.string().nullable(),
    clientDisclosureRequired: z.boolean(),
    disclosureCompletedAt: z.string().nullable(),
    disclosureCompletedByUserId: z.string().nullable(),
    disclosureNote: z.string().nullable(),
    complianceStatus: conflictComplianceStatusSchema,
    complianceComments: z.string().nullable(),
    complianceReviewedByUserId: z.string().nullable(),
    complianceReviewedAt: z.string().nullable(),
    recordedAt: z.string(),
    recordedByUserId: z.string().nullable(),
    /** The directory name of the person who recorded it, or null when none resolves. */
    recordedByName: z.string().nullable(),
    sourceUploadIds: z.array(z.string()),
});
export type OnboardingConflictRecord = z.infer<typeof onboardingConflictRecordSchema>;

/** The client categories the firm uses (SEP-243 section 10). */
export const clientClassificationSchema = z.enum([
    'retail',
    'professional',
    'elective_professional',
    'eligible_counterparty',
]);
export type ClientClassification = z.infer<typeof clientClassificationSchema>;

export const classificationAssessmentStatusSchema = z.enum([
    'pending',
    'approved',
    'rejected',
    'amended',
]);
export type ClassificationAssessmentStatus = z.infer<typeof classificationAssessmentStatusSchema>;

/** The classification and HNW record of one case (SEP-243 section 10). */
export const clientClassificationRecordSchema = z.object({
    id: z.string(),
    caseId: z.string(),
    clientId: z.string().nullable(),
    proposedClassification: clientClassificationSchema,
    classificationBasis: z.string().nullable(),
    hnwSelected: z.boolean(),
    hnwExplanation: z.string().nullable(),
    /** The copy entry that held the HNW definition. Null for a placeholder. */
    hnwDefinitionCopyEntryId: z.string().nullable(),
    recordedAt: z.string(),
    recordedByUserId: z.string().nullable(),
    /** The directory name of the person who recorded it, or null when none resolves. */
    recordedByName: z.string().nullable(),
    assessmentStatus: classificationAssessmentStatusSchema,
    assessmentComments: z.string().nullable(),
    assessedByUserId: z.string().nullable(),
    assessedAt: z.string().nullable(),
    finalClassification: clientClassificationSchema.nullable(),
    finalHnwStatus: z.boolean().nullable(),
    sourceUploadIds: z.array(z.string()),
});
export type ClientClassificationRecord = z.infer<typeof clientClassificationRecordSchema>;

/**
 * What the approved Internal Review says about the classification of the
 * client (SEP-343 items 4 and 6). The form opens with these values, and the
 * card sets the two figures against the two high net worth tests.
 */
export const approvedAssessmentClassificationSchema = z.object({
    assessmentId: z.string(),
    /** The classification of the review. Null when the review states none. */
    classification: z.enum(['retail', 'professional', 'hnw']).nullable(),
    /** The annual income of the review, in pounds. */
    annualIncome: z.number(),
    /** The net assets of the review in pounds, without the home and the pensions. */
    netAssets: z.number(),
});
export type ApprovedAssessmentClassification = z.infer<
    typeof approvedAssessmentClassificationSchema
>;

/**
 * The four registers of one case, in one read.
 *
 * The case panel needs all four together, so one endpoint carries them. The
 * summaries are DERIVED, so this response never holds them: the reader calls
 * `summariseProvidedDocuments` and `summariseReceivedDocuments`.
 *
 * `classification` is the NEWEST record of the case, and `classificationHistory`
 * holds every record including that one. The register is append-only, so a
 * change of mind is a new row and the file keeps both.
 */
export const onboardingRegistersResponseSchema = z.object({
    provided: z.array(providedDocumentEntrySchema),
    /**
     * The acts of delivery of the case, newest first (SEP-253). A row of
     * `provided` names the act that settled it through its `sendId`.
     */
    sends: z.array(providedDocumentSendSchema),
    /**
     * The LIVE version label of each catalogue code the firm has published
     * (SEP-253 request 5). The form fills its version field from this, so the
     * investment manager never types a firm fact from memory.
     */
    documentVersions: z.array(firmDocumentVersionSchema),
    received: z.array(receivedDocumentRequirementSchema),
    conflicts: z.array(onboardingConflictRecordSchema),
    classification: clientClassificationRecordSchema.nullable(),
    classificationHistory: z.array(clientClassificationRecordSchema),
    /**
     * The service type that the client chose in the application (SEP-343 item
     * 2). Null when the case holds no submitted application.
     */
    serviceType: packServiceTypeSchema.nullable(),
    /** The approved Internal Review of the client, or null when none is approved. */
    approvedAssessment: approvedAssessmentClassificationSchema.nullable(),
    /** Every item of the copy seam, already resolved against the register. */
    copy: z.array(resolvedComplianceCopySchema),
});
export type OnboardingRegistersResponse = z.infer<typeof onboardingRegistersResponseSchema>;

export const openProvidedDocumentRequestSchema = z.object({
    /** A code of `PROVIDED_DOCUMENT_CATALOGUE`. */
    documentCode: z.string().min(1),
});
export type OpenProvidedDocumentRequest = z.infer<typeof openProvidedDocumentRequestSchema>;

export const recordProvidedDocumentRequestSchema = z.object({
    provided: providedDocumentAnswerSchema,
    versionLabel: z.string().nullable().optional(),
    dateProvided: z.string().nullable().optional(),
    deliveryMethod: providedDocumentDeliveryMethodSchema.nullable().optional(),
    notes: z.string().nullable().optional(),
    acknowledgementReceivedAt: z.string().nullable().optional(),
    acknowledgementReference: z.string().nullable().optional(),
});
export type RecordProvidedDocumentRequest = z.infer<typeof recordProvidedDocumentRequestSchema>;

/**
 * ONE ACT OF DELIVERY (SEP-253).
 *
 * The caller names the pack and the act, and nothing else: the labels, the
 * mandatory flags and the version labels come from the catalogue and from the
 * firm's own version register at the controller. `versionOverrides` is the one
 * escape hatch, for the day the client got a copy that is not the live one.
 */
export const recordDocumentSendRequestSchema = z.object({
    /** The plain calendar day the firm sent the pack, `YYYY-MM-DD`. */
    sentOn: z.string().min(1),
    deliveryMethod: providedDocumentDeliveryMethodSchema,
    /**
     * The pack that went out (SEP-256 request 4). The service type decides
     * which documents the firm gives. It is optional, because an act may carry
     * one document that belongs to no pack.
     */
    serviceType: packServiceTypeSchema.nullable().optional(),
    /**
     * The codes the pack carried. A code of `PROVIDED_DOCUMENT_CATALOGUE`, or
     * the code of a LIVE FirmDocument the firm published itself.
     */
    documentCodes: z.array(z.string()).min(1),
    /** A label that differs from the firm's live one, for one code. */
    versionOverrides: z
        .array(z.object({ documentCode: z.string(), versionLabel: z.string().nullable() }))
        .optional(),
    reference: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
});
export type RecordDocumentSendRequest = z.infer<typeof recordDocumentSendRequestSchema>;

/** A version correction is NOT a re-record (SEP-253). */
export const correctProvidedDocumentVersionRequestSchema = z.object({
    versionLabel: z.string().nullable(),
});
export type CorrectProvidedDocumentVersionRequest = z.infer<
    typeof correctProvidedDocumentVersionRequestSchema
>;

export const openReceivedDocumentRequestSchema = z.object({
    /** An OPEN string: the ticket does not close the set of types. */
    documentType: z.string().min(1),
    notes: z.string().nullable().optional(),
});
export type OpenReceivedDocumentRequest = z.infer<typeof openReceivedDocumentRequestSchema>;

export const recordReceivedDocumentRequestSchema = z.object({
    status: z.enum(['received', 'na', 'outstanding']),
    dateReceived: z.string().nullable().optional(),
    /** The ids the client Documents door minted for this requirement. */
    sourceUploadIds: z.array(z.string()).default([]),
    notes: z.string().nullable().optional(),
    naReason: z.string().nullable().optional(),
});
export type RecordReceivedDocumentRequest = z.infer<typeof recordReceivedDocumentRequestSchema>;

export const checkReceivedDocumentRequestSchema = z.object({
    checkedDate: z.string().min(1),
    notes: z.string().nullable().optional(),
});
export type CheckReceivedDocumentRequest = z.infer<typeof checkReceivedDocumentRequestSchema>;

export const recordConflictRequestSchema = z.object({
    conflictIdentified: z.enum(['yes', 'no']),
    conflictType: z.string().nullable().optional(),
    conflictTypeCopyEntryId: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    partiesInvolved: z.array(z.string()).default([]),
    proposedMitigation: z.string().nullable().optional(),
    clientDisclosureRequired: z.boolean().default(false),
    sourceUploadIds: z.array(z.string()).default([]),
});
export type RecordConflictRequest = z.infer<typeof recordConflictRequestSchema>;

export const reviewConflictRequestSchema = z.object({
    status: z.enum(['approved', 'rejected']),
    comments: z.string().min(1),
});
export type ReviewConflictRequest = z.infer<typeof reviewConflictRequestSchema>;

export const completeConflictDisclosureRequestSchema = z.object({
    note: z.string().min(1),
});
export type CompleteConflictDisclosureRequest = z.infer<
    typeof completeConflictDisclosureRequestSchema
>;

export const recordClassificationRequestSchema = z.object({
    proposedClassification: clientClassificationSchema,
    classificationBasis: z.string().nullable().optional(),
    hnwSelected: z.boolean(),
    hnwExplanation: z.string().nullable().optional(),
    hnwDefinitionCopyEntryId: z.string().nullable().optional(),
    sourceUploadIds: z.array(z.string()).default([]),
});
export type RecordClassificationRequest = z.infer<typeof recordClassificationRequestSchema>;

export const assessClassificationRequestSchema = z.object({
    status: z.enum(['approved', 'rejected', 'amended']),
    finalClassification: clientClassificationSchema.nullable().optional(),
    finalHnwStatus: z.boolean().nullable().optional(),
    comments: z.string().min(1),
});
export type AssessClassificationRequest = z.infer<typeof assessClassificationRequestSchema>;

export const publishComplianceCopyRequestSchema = z.object({
    scope: complianceCopyScopeSchema,
    code: z.string().min(1),
    title: z.string().min(1),
    body: z.string().min(1),
    options: z.array(z.string()).nullable().optional(),
    publishNote: z.string().nullable().optional(),
});
export type PublishComplianceCopyRequest = z.infer<typeof publishComplianceCopyRequestSchema>;

export const complianceCopyResponseSchema = z.object({
    copy: z.array(resolvedComplianceCopySchema),
});
export type ComplianceCopyResponse = z.infer<typeof complianceCopyResponseSchema>;

// ---------------------------------------------------------------------------
// Request / response schemas
// ---------------------------------------------------------------------------

export const onboardingListResponseSchema = z.object({
    cases: z.array(onboardingPipelineCaseSchema),
});
export type OnboardingListResponse = z.infer<typeof onboardingListResponseSchema>;

const onboardingCaseResponseSchema = z.object({
    ok: z.boolean(),
    case: onboardingPipelineCaseSchema,
});
export type OnboardingCaseResponse = z.infer<typeof onboardingCaseResponseSchema>;

/**
 * The answer to an invitation send (SEP-289). It carries the link beside the
 * case, so the IM or the EA can give the link to the client by hand.
 *
 * Septima sends the email from the mailbox of the person who sends (SEP-378).
 * `emailDelivery` says what happened: `sent`, `not_sent` (that person has no
 * connected Outlook, or the server has no Outlook) or `failed`. `emailError`
 * gives the reason when the email did not go. The invitation stands in all
 * three events, and the link works.
 */
const inviteApplicationFormResponseSchema = onboardingCaseResponseSchema.extend({
    invitationUrl: z.string(),
    emailDelivery: z.enum(['sent', 'not_sent', 'failed']),
    emailError: z.string().nullable(),
});
export type InviteApplicationFormResponse = z.infer<typeof inviteApplicationFormResponseSchema>;

export const createEnquiryRequestSchema = z.object({
    prospectName: z.string().min(1),
    applicantType: z.enum(['Individual', 'Joint', 'Trust', 'Entity']).default('Individual'),
    // The five STAFF origins (decision D11). `Online client application` is
    // not one of them: the client-led route stamps that value on the server,
    // so a staff request can never claim it.
    source: staffEnquirySourceSchema,
    referrer: z.string().optional(),
    /** Null when the IM gives no figure. */
    initialAum: z.number().nonnegative().nullable().default(null),
    mandate: z.string().default('Balanced Growth'),
    /**
     * The ISA answer, in three states (SEP-243 section 2). `not_asked` is the
     * value of an enquiry that never put the question, and it is NOT the same
     * as `no`. It defaults to `not_asked`, so an older client that sends
     * nothing states the truth and does not claim a refusal.
     */
    isaSelection: isaSelectionSchema.default('not_asked'),
    /** The objective the client states, from the closed set of three. */
    investmentObjective: investmentObjectiveSchema.optional(),
    /**
     * The risk profile the CLIENT intends. It is a preference, and it is never
     * the IM's recommendation: nothing may copy it into
     * `riskProfileRecommendation` (SEP-243 section 24).
     */
    intendedRiskProfile: riskProfileSchema.optional(),
    // The intake asked whether the client owns a business until SEP-263
    // decision D5 moved the question to the fact find, which is the one door
    // that asks it now. The case column survives and stays readable, because a
    // case raised before the move holds the answer of its own intake; this
    // request writes it no more.
    /**
     * The prospect's email. Optional: an enquiry taken over the telephone may
     * carry a number and no address. It used to be required, so the dialog
     * invented "prospect@example.com", which made every such enquiry resolve to
     * the same Client (SEP-203).
     */
    email: z.string().optional(),
    phone: z.string().optional(),
    /**
     * Optional preferred IM name. It is a HINT only: the server derives the
     * stored name from the resolved IM id, and it reads this field solely when
     * no id resolves (an older client that sends a name alone). The field stays
     * on the wire for that compatibility (SEP-225).
     */
    preferredIm: z.string().optional(),
    /**
     * Stable id of the preferred IM (the `users` row), captured when chosen.
     * The server resolves the true owner from it: a desk caller always stamps
     * their own book owner (a junior EA resolves onto the senior they act for),
     * and every other caller has this id validated against the Active senior
     * desk roster. When no IM resolves, the case awaits triage.
     */
    preferredImId: z.string().optional(),
});
export type CreateEnquiryRequest = z.infer<typeof createEnquiryRequestSchema>;

/**
 * Correct a prospect's contact details after intake (SEP-203). Until this
 * existed, a telephone number captured at the first meeting could never be
 * added to the case, so the prospect's recorded calls could never be matched.
 *
 * An empty string CLEARS the field; an absent key leaves it alone.
 */
export const updateContactDetailsRequestSchema = z.object({
    phone: z.string().optional(),
    email: z.string().optional(),
});
export type UpdateContactDetailsRequest = z.infer<typeof updateContactDetailsRequestSchema>;

export const assignImRequestSchema = z.object({
    imName: z.string().min(1),
    imId: z.string().optional(),
    // Ignored. The server names the actor from the session, never from the body.
    by: z.string().optional(),
});
export type AssignImRequest = z.infer<typeof assignImRequestSchema>;

export const transitionRequestSchema = z.object({
    to: onboardingPipelineStageSchema,
    // Ignored. The server names the actor from the session, never from the body.
    by: z.string().optional(),
    note: z.string().optional(),
});
export type TransitionRequest = z.infer<typeof transitionRequestSchema>;

/**
 * The post-meeting note the IM captures at the first meeting.
 *
 * SEP-243 took FOUR fields off this request, and each one now has a register of
 * its own. `documentsHandedOver` was one comma-separated text box, and
 * ProvidedDocumentEntry replaces it (section 4). `conflictsCheck` was one free
 * text box, and OnboardingConflictRecord replaces it (section 11).
 * `classification` and `hnwStatus` were one two-value select and one tick, and
 * ClientClassificationRecord replaces them (section 10): section 9 asks for ONE
 * authoritative classification record, and two writable surfaces on one page
 * are two answers that can disagree.
 *
 * All four stay on `postMeetingNoteSchema` as optional fields, because a case
 * that the firm captured before this change still holds their values and a
 * reader must still see them.
 */
export const postMeetingNoteRequestSchema = z.object({
    // Ignored. The server names the actor from the session, never from the body.
    capturedBy: z.string().optional(),
    clientAgenda: z.string().min(1),
});
export type PostMeetingNoteRequest = z.infer<typeof postMeetingNoteRequestSchema>;

/**
 * Send the client a link to the online application form (SEP-289). The name
 * and the email address default to the case on the page, and the person who
 * sends can correct them. The form opens with both filled in.
 */
export const inviteApplicationFormRequestSchema = z.object({
    forenames: z.string().trim().min(1),
    surname: z.string().trim().min(1),
    email: z.string().trim().email(),
    /**
     * The email that the sender edited (SEP-378). The message is plain text,
     * and it must hold `APPLICATION_FORM_LINK_MARKER` where the link goes.
     * When both fields are absent, the server sends the template of
     * `applicationFormInvitationTemplate`.
     */
    subject: z.string().trim().min(1).optional(),
    message: z
        .string()
        .trim()
        .min(1)
        .refine(hasApplicationFormLinkMarker, {
            message: `Put ${APPLICATION_FORM_LINK_MARKER} in the message where the link goes`,
        })
        .optional(),
});
export type InviteApplicationFormRequest = z.infer<typeof inviteApplicationFormRequestSchema>;

/** Record a form the firm received outside Septima. The note is mandatory. */
export const recordApplicationFormReceivedOutsideRequestSchema = z.object({
    note: z.string().trim().min(1),
});
export type RecordApplicationFormReceivedOutsideRequest = z.infer<
    typeof recordApplicationFormReceivedOutsideRequestSchema
>;

export const remindRequestSchema = z.object({
    // Ignored. The server names the actor from the session, never from the body.
    by: z.string().optional(),
    channel: z.enum(['email', 'sms']).default('email'),
    /**
     * Send the templated reminder email from the server. The Desk fact-find
     * panel opens an Outlook draft that the IM sends themself, then records the
     * reminder with `sendEmail: false`, so the prospect gets one message and not
     * two. Defaults to true: an API caller that has no draft of its own keeps
     * the server send.
     */
    sendEmail: z.boolean().default(true),
});
export type RemindRequest = z.infer<typeof remindRequestSchema>;

// `by` is ignored: the server names the actor from the session.
export const actorRequestSchema = z.object({ by: z.string().optional() });
export type ActorRequest = z.infer<typeof actorRequestSchema>;

/**
 * The paper override of the fact find (SEP-293 section 7). It is for a fact
 * find that the client completed outside the platform, and the reason is
 * mandatory, because the override completes the branch with no approval.
 */
export const factFindOverrideRequestSchema = z.object({
    by: z.string().optional(),
    reason: z.string().trim().min(1),
});
export type FactFindOverrideRequest = z.infer<typeof factFindOverrideRequestSchema>;

/** Remove a case from the pipeline — the reason note is mandatory. */
export const removeCaseRequestSchema = z.object({
    reason: z.string().trim().min(1),
    // Ignored. The server names the actor from the session, never from the body.
    by: z.string().optional(),
});
export type RemoveCaseRequest = z.infer<typeof removeCaseRequestSchema>;

// ---------------------------------------------------------------------------
// Reuse-or-run call dispatch (§7 First Meeting knowledge)
// ---------------------------------------------------------------------------

/**
 * First-Meeting Client-Knowledge prefill from a call. When the call already
 * carries an `aiSummary` / Client-Knowledge note, the post-meeting agenda is
 * prefilled from it (`reused: true`) instead of re-dispatching ATLAS.
 */
export const onboardingCallKnowledgeResponseSchema = z.object({
    /** True when an existing artefact was reused; false when ATLAS was dispatched. */
    reused: z.boolean(),
    /** Prefill text from the existing artefact — present only when `reused` is true. */
    prefill: z
        .object({
            agenda: z.string(),
            conflicts: z.string(),
        })
        .optional(),
});
export type OnboardingCallKnowledgeResponse = z.infer<typeof onboardingCallKnowledgeResponseSchema>;

export const onboardingStageMetricSchema = z.object({
    stage: onboardingPipelineStageSchema,
    count: z.number().int(),
    medianDays: z.number(),
    slaDays: z.number().int(),
    breachedSla: z.number().int(),
});
export type OnboardingStageMetric = z.infer<typeof onboardingStageMetricSchema>;

export const onboardingAnalyticsResponseSchema = z.object({
    totalCases: z.number().int(),
    activeCases: z.number().int(),
    completedCases: z.number().int(),
    stalledCases: z.number().int(),
    conversionRate: z.number(),
    stages: z.array(onboardingStageMetricSchema),
    imPerformance: z.array(
        z.object({
            im: z.string(),
            activeCases: z.number().int(),
            stalledCases: z.number().int(),
            completedCases: z.number().int(),
        }),
    ),
});
export type OnboardingAnalyticsResponse = z.infer<typeof onboardingAnalyticsResponseSchema>;

import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const onboardingContract = c.router(
    {
        listCases: {
            method: 'GET',
            path: '/onboarding/cases',
            query: z
                .object({
                    stage: onboardingPipelineStageSchema.optional(),
                    imId: z.string().optional(),
                    status: onboardingCaseStatusSchema.optional(),
                })
                .optional(),
            responses: { 200: onboardingListResponseSchema },
            summary: 'List onboarding pipeline cases',
        },
        getCase: {
            method: 'GET',
            path: '/onboarding/cases/:id',
            responses: { 200: onboardingCaseResponseSchema, 404: errorBody },
            summary: 'Get a single onboarding case',
        },
        // SEP-293 section 7. The state of the three sub-branches of the
        // Internal Review of one client. The header of the fact find, the
        // vulnerability and the suitability pages reads it.
        internalReview: {
            method: 'GET',
            path: '/onboarding/internal-review/:clientId',
            responses: {
                200: z.object({ branches: internalReviewBranchesSchema }),
                404: errorBody,
            },
            summary: 'The state of the three sub-branches of the Internal Review of one client',
        },
        // SEP-293 section 10. The progress of the portal acknowledgement of
        // each document of one case, for the panel of the IM and the EA.
        caseDocumentAcknowledgements: {
            method: 'GET',
            path: '/onboarding/cases/:id/document-acknowledgements',
            responses: {
                200: caseDocumentAcknowledgementsResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'The open and acknowledgement state of each document of one case in the client portal (SEP-293 section 10)',
        },
        createEnquiry: {
            method: 'POST',
            path: '/onboarding/cases',
            body: createEnquiryRequestSchema,
            responses: { 200: onboardingCaseResponseSchema, 400: errorBody },
            summary: 'Create a new stage-1 enquiry (assigned to the chosen IM, else unassigned)',
        },
        updateContactDetails: {
            method: 'PATCH',
            path: '/onboarding/cases/:id/contact-details',
            body: updateContactDetailsRequestSchema,
            responses: { 200: onboardingCaseResponseSchema, 400: errorBody, 404: errorBody },
            summary:
                "Correct the prospect's telephone number or email, and propagate them onto the prospect Client",
        },
        assignIm: {
            method: 'POST',
            path: '/onboarding/cases/:id/assign-im',
            body: assignImRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Assign (or reassign) the investment manager on a case — ops and comply only (SEP-218)',
        },
        markReadyForAml: {
            method: 'POST',
            path: '/onboarding/cases/:id/ready-for-aml',
            body: z.object({}),
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Hand a case to the Ops AML and KYC queue. The suitability work then runs in parallel (SEP-243 section 16)',
        },
        listOpenedAccounts: {
            method: 'GET',
            path: '/onboarding/cases/:id/opened-accounts',
            responses: { 200: openedAccountListResponseSchema, 403: errorBody, 404: errorBody },
            summary: 'List the accounts that Operations opened for the client of a case (SEP-337)',
        },
        recordOpenedAccount: {
            method: 'POST',
            path: '/onboarding/cases/:id/opened-accounts',
            body: recordOpenedAccountRequestSchema,
            responses: {
                200: z.object({ ok: z.boolean(), account: openedAccountSchema }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Operations records an account that it opened for the client of a case (SEP-337)',
        },
        correctOpenedAccount: {
            method: 'PATCH',
            path: '/onboarding/cases/:id/opened-accounts/:accountId',
            body: correctOpenedAccountRequestSchema,
            responses: {
                200: z.object({ ok: z.boolean(), account: openedAccountSchema }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Operations corrects an account that it recorded, with a reason (SEP-337)',
        },
        runAmlVendor: {
            method: 'POST',
            path: '/onboarding/cases/:id/aml/run',
            body: runAmlVendorRequestSchema,
            responses: {
                200: runAmlVendorResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
                // 503: the firm holds no key for the KYC and AML vendor, so no
                // screening can happen. The endpoint refuses; it never invents
                // a verdict (ADR-0020).
                503: errorBody,
            },
            summary:
                'Ops runs the KYC and AML vendor on a case from the Ready for AML and KYC queue. A pass clears the track; a refer or a fail opens a compliance referral (SEP-243 section 14)',
        },
        listAmlReferrals: {
            method: 'GET',
            path: '/onboarding/aml-referrals',
            query: z.object({
                status: z.enum(['open', 'resolved']).optional(),
                caseId: z.string().optional(),
            }),
            // The read ACL of AmlCaseReferral admits Ops and Compliance only
            // (SEP-243 section 14). A Desk actor gets a real 403, and not an
            // empty list: an empty list reads as "no referral", which is a
            // different fact.
            responses: { 200: amlReferralListResponseSchema, 403: errorBody },
            summary: 'List the AML referrals, newest first. Ops and Compliance read them both',
        },
        recordAmlReferralNote: {
            method: 'POST',
            path: '/onboarding/aml-referrals/:id/notes',
            body: recordAmlReferralNoteRequestSchema,
            responses: {
                200: amlReferralResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Append one note, or one request for information, to an open AML referral (SEP-243 section 15)',
        },
        resolveAmlReferral: {
            method: 'POST',
            path: '/onboarding/aml-referrals/:id/resolve',
            body: resolveAmlReferralRequestSchema,
            responses: {
                200: amlReferralResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Compliance resolves one AML referral. Resolving the LAST open referral clears the AML track of the case (SEP-243 section 15)',
        },
        removeCase: {
            method: 'POST',
            path: '/onboarding/cases/:id/remove',
            body: removeCaseRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Remove a case from the pipeline (retained for audit, hidden from the board) — reason required',
        },
        transition: {
            method: 'POST',
            path: '/onboarding/cases/:id/transition',
            body: transitionRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
                409: transitionRefusalSchema,
            },
            summary: 'Move a case between stages (prerequisites + stage-owner role enforced)',
        },
        // SEP-293 section 11. What holds the move of one case into Signing.
        // The server stays the gate: the transition gives 409
        // `signing_blocked` with the same blockers.
        signingReadiness: {
            method: 'GET',
            path: '/onboarding/cases/:id/signing-readiness',
            responses: { 200: signingReadinessSchema, 403: errorBody, 404: errorBody },
            summary:
                'The blockers of the move of one case into Finalisation & Sign-off (SEP-293 section 11)',
        },
        // SEP-289 - the application form stage.
        inviteApplicationForm: {
            method: 'POST',
            path: '/onboarding/cases/:id/application-form/invitation',
            body: inviteApplicationFormRequestSchema,
            responses: {
                200: inviteApplicationFormResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Send the client a link to the online application form, bound to this case. A second send replaces the link (SEP-289)',
        },
        recordApplicationFormReceivedOutside: {
            method: 'POST',
            path: '/onboarding/cases/:id/application-form/received-outside',
            body: recordApplicationFormReceivedOutsideRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Record that the firm received the application form of this case outside Septima, with a note (SEP-289)',
        },
        capturePostMeetingNote: {
            method: 'POST',
            path: '/onboarding/cases/:id/post-meeting-note',
            body: postMeetingNoteRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Capture a structured stage-2 post-meeting note',
        },
        remindFactFind: {
            method: 'POST',
            path: '/onboarding/cases/:id/fact-find/remind',
            body: remindRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Send a stage-3 fact-find reminder (email/SMS stub)',
        },
        markFactFindComplete: {
            method: 'POST',
            path: '/onboarding/cases/:id/fact-find/complete',
            body: factFindOverrideRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Mark the stage-3 fact find complete',
        },
        // SEP-346. Ops finishes the onboarding of a case at Account opening.
        // It gives the portal login, moves the client into the Client Book,
        // publishes the final pack and sends the welcome email.
        completeCase: {
            method: 'POST',
            path: '/onboarding/cases/:id/complete',
            body: actorRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Finish the onboarding of one case at Account opening (SEP-346)',
        },
        prefillKnowledgeFromCall: {
            method: 'POST',
            path: '/onboarding/cases/:id/calls/:callId/knowledge',
            body: z.object({}).optional(),
            responses: {
                200: onboardingCallKnowledgeResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'First Meeting: reuse the call’s Client-Knowledge for the post-meeting note, or dispatch ATLAS when absent',
        },
        // --- SEP-243 slice 7: the registers, the conflicts, the
        // classification and the Compliance copy seam ---
        //
        // ONE read carries all four registers, because the case panel needs all
        // four together and four round trips would tear the picture apart.
        caseRegisters: {
            method: 'GET',
            path: '/onboarding/cases/:id/registers',
            responses: {
                200: onboardingRegistersResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'The four registers of one case: the documents provided, the documents received, the conflicts and the classification, with the Compliance copy already resolved (SEP-243 sections 4, 5, 10 and 11)',
        },
        openProvidedDocument: {
            method: 'POST',
            path: '/onboarding/cases/:id/documents-provided',
            body: openProvidedDocumentRequestSchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Put one catalogue item on the provided-documents register of a case (SEP-243 section 4)',
        },
        recordProvidedDocument: {
            method: 'POST',
            path: '/onboarding/documents-provided/:entryId',
            body: recordProvidedDocumentRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Record that the firm provided one document, with the date and the method (SEP-243 section 4)',
        },
        // SEP-253 - the register records one ACT of delivery, and not six
        // documents. One send settles the whole pack, one undo takes it back,
        // and a version correction moves nothing else.
        recordDocumentSend: {
            method: 'POST',
            path: '/onboarding/cases/:id/document-sends',
            body: recordDocumentSendRequestSchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Record ONE act of delivery: the firm gave a pack of documents to the client on one day, by one method (SEP-253)',
        },
        withdrawDocumentSend: {
            method: 'POST',
            path: '/onboarding/document-sends/:sendId/withdraw',
            body: z.object({}).optional(),
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Take back one act of delivery, and reset every register row it settled. The send row survives, withdrawn (SEP-253)',
        },
        correctProvidedDocumentVersion: {
            method: 'POST',
            path: '/onboarding/documents-provided/:entryId/version',
            body: correctProvidedDocumentVersionRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Correct the version label of one provided document, and change nothing else (SEP-253)',
        },
        openReceivedDocument: {
            method: 'POST',
            path: '/onboarding/cases/:id/documents-received',
            body: openReceivedDocumentRequestSchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Ask the client for one document. The requirement opens as outstanding, with no file (SEP-243 section 5)',
        },
        recordReceivedDocument: {
            method: 'POST',
            path: '/onboarding/documents-received/:entryId',
            body: recordReceivedDocumentRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Record that one requested document arrived, or that it does not apply (SEP-243 section 5)',
        },
        checkReceivedDocument: {
            method: 'POST',
            path: '/onboarding/documents-received/:entryId/check',
            body: checkReceivedDocumentRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Record that a member of staff checked one received document (SEP-243 section 5)',
        },
        recordConflict: {
            method: 'POST',
            path: '/onboarding/cases/:id/conflicts',
            body: recordConflictRequestSchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Answer the conflicts question on one case (SEP-243 section 11)',
        },
        reviewConflict: {
            method: 'POST',
            path: '/onboarding/conflicts/:entryId/review',
            body: reviewConflictRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                // 403: only Compliance reviews a conflict. The person who
                // records one must not approve it.
                403: errorBody,
                404: errorBody,
            },
            summary: 'Record the Compliance position on one conflict (SEP-243 section 11)',
        },
        completeConflictDisclosure: {
            method: 'POST',
            path: '/onboarding/conflicts/:entryId/disclosure',
            body: completeConflictDisclosureRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Record that the firm disclosed one conflict to the client (SEP-243 section 11)',
        },
        recordClassification: {
            method: 'POST',
            path: '/onboarding/cases/:id/classification',
            body: recordClassificationRequestSchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Propose the classification of one client, with the HNW selection and its explanation (SEP-243 section 10)',
        },
        assessClassification: {
            method: 'POST',
            path: '/onboarding/classification/:entryId/assess',
            body: assessClassificationRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                // 403: only Compliance decides the final classification.
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Record the Compliance assessment of one classification, and the final answer (SEP-243 section 10)',
        },
        complianceCopy: {
            method: 'GET',
            path: '/onboarding/compliance-copy',
            responses: { 200: complianceCopyResponseSchema, 403: errorBody },
            summary:
                'Every item of the Compliance copy seam, resolved against the published register. An item with no published entry comes back as a placeholder that names itself (SEP-243 decision D4)',
        },
        publishComplianceCopy: {
            method: 'POST',
            path: '/onboarding/compliance-copy',
            body: publishComplianceCopyRequestSchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                // 403: Compliance owns the wording, and nobody else writes it.
                403: errorBody,
            },
            summary:
                'Publish one item of Compliance-owned wording. The active entry is superseded, and nothing is edited in place (SEP-243 decision D4)',
        },
        analytics: {
            method: 'GET',
            path: '/onboarding/analytics',
            query: z.object({ imId: z.string().optional() }).optional(),
            responses: { 200: onboardingAnalyticsResponseSchema },
            summary: 'Pipeline analytics rollup (funnel, median days, SLA, stalls)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
